import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * AI Assistant Store
 * Manages chat sessions, messages, and AI Assistant state
 */

// Response format types - defines how the AI formats its response
export type ResponseFormatType =
  | 'mbb_pyramid'      // Strategic/analytical - Pyramid Principle structure (MBB consulting style)
  | 'conversational'   // Simple questions - natural, friendly response
  | 'structured'       // Lists, comparisons - bullet points, tables
  | 'technical'        // Code examples, technical explanations
  | 'fallback'         // Error fallback
  | 'text';            // Legacy text format

export interface QueryClassification {
  intent: string;
  contexts: string[];
  entities: {
    companies?: string[];
    domains?: string[];
    systems?: string[];
  };
  dataSourcesRecommended: string[];
  complexity: string;
  responseFormat?: ResponseFormatType;
  confidence: number;
  reasoning: string;
}

export interface OrchestrationDetails {
  sourcesQueried: string[];
  sourcesSucceeded: string[];
  sourcesFailed: string[];
  neo4jQuery?: string;
  webSearchQuery?: string;
  parallelExecution: boolean;
  totalDurationMs: number;
}

export interface ChatMessage {
  messageId: string;
  sessionId: string;
  role: 'user' | 'assistant';
  content: string;
  responseFormat?: ResponseFormatType;
  sources?: string[];
  confidence?: {
    overall_score: number;
    overall_level: 'High' | 'Medium' | 'Low';
  };
  executionTimeMs?: number;
  feedback?: {
    rating: 'thumbs_up' | 'thumbs_down';
    comment?: string;
  };
  followUpQuestions?: string[];
  timestamp: string;
  createdAt: string;
  // Detailed execution metadata
  queryClassification?: QueryClassification;
  orchestrationDetails?: OrchestrationDetails;
}

export interface ChatSession {
  sessionId: string;
  userId: string;
  title: string; // Auto-generated by backend
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessage?: string;
  messages?: ChatMessage[]; // Optional, populated when include_messages=true
}

interface AIAssistantState {
  // Sessions state
  sessions: ChatSession[];
  currentSessionId: string | null;

  // Messages state
  messages: ChatMessage[];

  // Streaming state
  isStreaming: boolean;
  streamingMessageId: string | null;
  streamingContent: string;

  // UI state
  isLoading: boolean;
  error: string | null;
  chatHistorySidebarOpen: boolean;

  // Actions
  setSessions: (sessions: ChatSession[]) => void;
  addSession: (session: ChatSession) => void;
  updateSession: (sessionId: string, updates: Partial<ChatSession>) => void;
  setCurrentSession: (sessionId: string | null) => void;

  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
  clearMessages: () => void;

  // Streaming actions
  startStreaming: (messageId: string) => void;
  appendStreamContent: (chunk: string) => void;
  finishStreaming: (metadata?: Partial<ChatMessage>) => void;

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  toggleChatHistorySidebar: () => void;
  setChatHistorySidebarOpen: (open: boolean) => void;

  // Helper to create new session
  createNewSession: (userId: string) => string;
}

export const useAIAssistantStore = create<AIAssistantState>()(
  persist(
    (set, get) => ({
      // Initial state
      sessions: [],
      currentSessionId: null,
      messages: [],
      isStreaming: false,
      streamingMessageId: null,
      streamingContent: '',
      isLoading: false,
      error: null,
      chatHistorySidebarOpen: true,

      // Session actions
      setSessions: (sessions) => set({ sessions }),

      addSession: (session) =>
        set((state) => ({
          sessions: [session, ...state.sessions],
        })),

      updateSession: (sessionId, updates) =>
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.sessionId === sessionId ? { ...s, ...updates } : s
          ),
        })),

      setCurrentSession: (sessionId) => set({ currentSessionId: sessionId }),

      // Message actions
      setMessages: (messages) => set({ messages }),

      addMessage: (message) =>
        set((state) => ({
          messages: [...state.messages, message],
        })),

      updateMessage: (messageId, updates) =>
        set((state) => ({
          messages: state.messages.map((m) =>
            m.messageId === messageId ? { ...m, ...updates } : m
          ),
        })),

      clearMessages: () => set({ messages: [] }),

      // Streaming actions
      startStreaming: (messageId) =>
        set({
          isStreaming: true,
          streamingMessageId: messageId,
          streamingContent: '',
        }),

      appendStreamContent: (chunk) =>
        set((state) => ({
          streamingContent: state.streamingContent + chunk,
        })),

      finishStreaming: (metadata) =>
        set((state) => {
          // Update the message with final content and metadata
          const updatedMessages = state.messages.map((m) =>
            m.messageId === state.streamingMessageId
              ? {
                  ...m,
                  content: state.streamingContent,
                  ...metadata,
                }
              : m
          );

          return {
            messages: updatedMessages,
            isStreaming: false,
            streamingMessageId: null,
            streamingContent: '',
          };
        }),

      // UI actions
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      toggleChatHistorySidebar: () =>
        set((state) => ({ chatHistorySidebarOpen: !state.chatHistorySidebarOpen })),
      setChatHistorySidebarOpen: (open) => set({ chatHistorySidebarOpen: open }),

      // Helper to create new session
      createNewSession: (userId) => {
        const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newSession: ChatSession = {
          sessionId,
          userId,
          title: 'New Conversation', // Will be updated by backend
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          messageCount: 0,
        };

        get().addSession(newSession);
        get().setCurrentSession(sessionId);
        get().clearMessages();

        return sessionId;
      },
    }),
    {
      name: 'ai-assistant-storage',
      partialize: (state) => ({
        // Persist sessions and current session
        sessions: state.sessions,
        currentSessionId: state.currentSessionId,
        chatHistorySidebarOpen: state.chatHistorySidebarOpen,
      }),
    }
  )
);
